import FileUpload from "@iewcasia/MobiusOverrides/FileUpload/FileUpload";
import setFieldValue from "@iewcasia/Store/Handlers/RfqRequestQuote/SetFieldValue";

import { CustomActions, CustomState } from "@iewcasia/Store/Reducers";
import { fields, OwnProps, setRequestFormEffects } from "@iewcasia/Widgets/RfqRequestQuote/FormBaseComponent";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface FormFieldStyles {
    wrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
}

const styles: FormFieldStyles = {
    wrapper: {
        css: css`
            padding: 20px 30px;
            background-color: #fff;
            display: flex;
            flex-direction: column;
            gap: 15px;
        `,
    } as any,
    headingText: {
        variant: "p",
        size: 18,
        weight: "bold",
        css: css`
            margin-bottom: 15px;
        `,
    },
};

const mapStateToProps = (state: CustomState) => {
    return {
        fileOne: state.customRequestQuoteForm.fieldValues.fileOne as unknown as File,
        fileTwo: state.customRequestQuoteForm.fieldValues.fileTwo as unknown as File,
    };
};

const mapDispatchToProps = {
    setFieldValue,
    setIsBadFile,
    setUploadLimitExceeded,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    DispatchProp<CustomActions>;

const FileUploadWidget: React.FC<Props> = ({
    fields: widgetFields,
    fileOne,
    fileTwo,
    setFieldValue: widgetSetFileValue,
    setIsBadFile: widgetSetIsBadFile,
    setUploadLimitExceeded: widgetSetUploadLimitExceeded,
}) => {
    const { heading, copy, isRequired, labelOne, labelTwo } = widgetFields;
    const [fileOneErrorMessage, setFileOneErrorMessagee] = React.useState("");
    const [fileTwoErrorMessage, setFileTwoErrorMessagee] = React.useState("");
    const fileUploadRef = React.useRef({ value: "" } as HTMLInputElement);
    const [fileOneField, setFileOneField] = React.useState<any>(null);
    const [fileTwoField, setFileTwoField] = React.useState<any>(null);
    const [isIncorrectFileOneSize, setIsIncorrectFileOneSize] = React.useState(false);
    const [isIncorrectFileTwoSize, setIsIncorrectFileTwoSize] = React.useState(false);
    const [isIncorrectFileOneExtension, setIsIncorrectFileOneExtension] = React.useState(false);
    const [isIncorrectFileTwoExtension, setIsIncorrectFileTwoExtension] = React.useState(false);
    const [inputKey1, setInputKey1] = useState(Date.now());
    const [inputKey2, setInputKey2] = useState(Date.now());

    const validate = (file: any, isIncorrectSize: any, isIncorrectExtension: any) => {
        let errorMessage = "";

        if (file && isIncorrectExtension) {
            errorMessage = translate("File type must be PDF");
            widgetSetIsBadFile({ isBadFile: true });
        } else if (file && isIncorrectSize) {
            errorMessage = translate("File size exceeds 3MB");
            widgetSetUploadLimitExceeded({ uploadLimitExceeded: true });
        }

        return errorMessage;
    };

    const validateFileUploadedFieldOne = () => {
        const errorMessage = validate(fileOneField, isIncorrectFileOneSize, isIncorrectFileOneExtension);
        setFileOneErrorMessagee(errorMessage as any);
        return !errorMessage;
    };

    const validateFileUploadedFieldTwo = () => {
        const errorMessage = validate(fileTwoField, isIncorrectFileTwoSize, isIncorrectFileTwoExtension);
        setFileTwoErrorMessagee(errorMessage as any);
        return !errorMessage;
    };

    setRequestFormEffects(isRequired, fileOne, "fileOne", validateFileUploadedFieldOne);
    setRequestFormEffects(isRequired, fileTwo, "fileTwo", validateFileUploadedFieldTwo);

    useEffect(() => {
        if (fileOne) {
            setIsIncorrectFileOneExtension(getFileExtension(fileOne.name)?.toLowerCase?.() !== "pdf");
            setIsIncorrectFileOneSize(fileOne.size > 3e6);
        } else {
            resetFileInput(1);
        }
    }, [fileOne]);

    useEffect(() => {
        if (fileTwo) {
            setIsIncorrectFileTwoExtension(getFileExtension(fileTwo.name)?.toLowerCase?.() !== "pdf");
            setIsIncorrectFileTwoSize(fileTwo.size > 3e6);
        } else {
            resetFileInput(2);
        }
    }, [fileTwo]);

    const resetFileInput = (order: number) => {
        // the file input element in the DOM is not being updated when the state changes. This is a quirk of how file inputs work in HTML.
        if (order === 1) {
            setInputKey1(Date.now());
        }
        if (order === 2) {
            setInputKey2(Date.now());
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <div>
                <Typography {...styles.headingText}>{heading}</Typography>
                {copy}
            </div>

            <div>
                <Typography weight="bold">{labelOne}</Typography>
                <FileUpload
                    inputRef={fileUploadRef}
                    fileName={fileOne?.name ?? ""}
                    accept=".pdf"
                    error={fileOneErrorMessage}
                    required={isRequired}
                    onFileChange={e => {
                        widgetSetFileValue({ key: "fileOne", value: e.target.files?.[0] });
                    }}
                    buttonText={translate("Add a file")}
                    placeholder={translate("No file selected")}
                    key={inputKey1}
                />
            </div>
            <div>
                <Typography weight="bold">{labelTwo}</Typography>
                <FileUpload
                    inputRef={fileUploadRef}
                    fileName={fileTwo?.name ?? ""}
                    accept=".pdf"
                    error={fileTwoErrorMessage}
                    required={isRequired}
                    onFileChange={e => {
                        widgetSetFileValue({ key: "fileTwo", value: e.target.files?.[0] });
                    }}
                    buttonText={translate("Add a file")}
                    placeholder={translate("No file selected")}
                    key={inputKey2}
                />
            </div>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FileUploadWidget),
    definition: {
        group: "Request a Quote Form" as any,
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Upload Spec/Additional Information",
                isRequired: true,
            },
            {
                name: fields.copy,
                displayName: "Copy",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue:
                    "Have a spec? Attach a PDF to your contact us request by clicking CHOOSE FILE below. (maximum size of 3MB)",
                isRequired: true,
            },
            {
                name: fields.labelOne,
                displayName: "Upload field 1 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Upload file 1",
                isRequired: true,
            },
            {
                name: fields.labelTwo,
                displayName: "Upload field 2 label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Upload file 2",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
